import PropTypes from 'prop-types';
import {
  A11y,
  Controller,
  Mousewheel,
  Navigation,
  Pagination,
  Autoplay,
} from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { getContent } from '@/utils/helper';
import {
  analytics,
  getCarouselNext,
  getCarouselPrevious,
} from '@/utils/analytics';

import Text from '@/atoms/Text/Text';
import Icon from '@/atoms/Icon/Icon';
import { DynamicAsset } from '@/molecules/Media/index';
import RichText from '@/molecules/RichText/RichText';

import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';

const SWIPER_MODULES = [
  A11y,
  Controller,
  Mousewheel,
  Navigation,
  Pagination,
  Autoplay,
];

const SWIPER_NAVIGATION = {
  nextEl: '.aaco-carousel-slider__next',
  prevEl: '.aaco-carousel-slider__prev',
};

const SWIPER_PAGINATION = {
  clickable: true,
  el: '.aaco-carousel-slider__pagination',
};

const SWIPER_SPEED = 1500;

function CarouselSlider({ assets, entries, fields }) {
  const {
    cards,
    showPagination,
    enableAutoLoop,
    durationOfEachSlide,
    removeYPadding,
    id,
  } = fields;

  const handleCarouselNext = (e) => {
    analytics({
      window,
      document,
      detail: getCarouselNext(e),
    });
  };

  const handleCarouselPrevious = (e) => {
    analytics({
      window,
      document,
      detail: getCarouselPrevious(e),
    });
  };
  return (
    <div
      id={id}
      className={`aaco-carousel-slider relative ${
        removeYPadding
          ? ''
          : 'dd-kr:mb-[80px] md:dd-kr:mb-[100px] dd-en:mb-[80px] md:dd-en:mb-[100px]'
      }`}
    >
      <Swiper
        modules={SWIPER_MODULES}
        mousewheel={{
          forceToAxis: true,
        }}
        spaceBetween={0}
        autoplay={
          enableAutoLoop ? { delay: durationOfEachSlide * 1000 } : false
        }
        speed={SWIPER_SPEED}
        loop={cards.length > 1 ? true : false}
        navigation={SWIPER_NAVIGATION}
        pagination={SWIPER_PAGINATION}
      >
        {cards.map((card, index) => {
          const content = getContent(card.sys?.id, entries);
          let { dynamicAsset, cloudinaryAssets, title, subtitle, text } =
            content.fields;
          text =
            text && text.sys
              ? getContent(text.sys.id, entries)?.fields?.text
              : text;
          return (
            <SwiperSlide key={index}>
              <div className="aaco-carousel-slider-card__slide relative flex justify-center">
                {(cloudinaryAssets || dynamicAsset) && (
                  <>
                    <div
                      className={`aaco-carousel-slider__content absolute z-10 text-center mx-[55px] md:mx-0 ${
                        !showPagination
                          ? 'bottom-[32px] md:bottom-[57px]'
                          : 'bottom-[107px] md:bottom-[112px]'
                      }`}
                    >
                      {subtitle && subtitle.length > 0 && (
                        <Text
                          tag={'h4'}
                          style={`text-transparent bg-clip-text bg-quality-gold dd-en:pb-5 dd-kr:pb-5 ${
                            title && title.length > 0
                              ? 'mb-[10px]'
                              : 'mb-[20px]'
                          }`}
                        >
                          {subtitle}
                        </Text>
                      )}
                      {title && title.length > 0 && (
                        <Text
                          tag={'h1'}
                          style={`h-full text-transparent bg-clip-text bg-quality-gold ${
                            text && text.length > 0 ? 'mb-[20px]' : ''
                          } md:dd-kr:text-h2 md:dd-en:text-h2 md:dd-kr:font-h2 md:dd-en:font-h2 dd-en:pb-[30px] dd-kr:pb-[30px]`}
                        >
                          {title}
                        </Text>
                      )}
                      {/* TODO(reza): 
                          on the line below, currently it need to render the text using dangerouslySetInnerHTML to enable line breaks, 
                          in the future might be better to use @contentful/rich-text-from-markdown lib instead.
                      */}
                      {text && !text.content && (
                        <p
                          className="font-text font-body text-body text-white block md:w-[600px] mx-auto"
                          dangerouslySetInnerHTML={{
                            __html: text.replace(/\n/g, '<br />'),
                          }}
                        ></p>
                      )}
                      {text &&
                        text.content &&
                        text.content.length > 0 &&
                        text.content.map(({ nodeType, content }, index) => (
                          <RichText
                            entries={entries}
                            nodeType={nodeType}
                            content={content}
                            key={index}
                            richTextStyle={{
                              paragraph: {
                                customFont: TEXT_BASE_CLASS['body'],
                              },
                            }}
                          />
                        ))}
                    </div>
                    <div className="aaco-card__image w-full">
                      <DynamicAsset
                        assets={assets}
                        entries={entries}
                        container="full"
                        field={cloudinaryAssets ?? dynamicAsset}
                        style={'h-[764px] md:h-[840px]'}
                      />
                      <div className="absolute bottom-0 bg-gradient-to-b from-transparent to-black/90 w-full h-4/5"></div>
                    </div>
                  </>
                )}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {cards.length > 1 && (
        <>
          <div
            className="aaco-carousel-slider__prev"
            onClick={handleCarouselPrevious}
          >
            <Icon
              name="chevron-left"
              height={22.75}
              width={11.38}
              className="text-white"
            />
          </div>
          <div
            className="aaco-carousel-slider__next"
            onClick={handleCarouselNext}
          >
            <Icon
              name="chevron-right"
              height={22.75}
              width={11.38}
              className="text-white"
            />
          </div>
          {showPagination && (
            <div className="aaco-carousel-slider__pagination"></div>
          )}
        </>
      )}
    </div>
  );
}

CarouselSlider.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

CarouselSlider.defaultProps = {
  assets: [],
  entries: [],
  fields: null,
};

export default CarouselSlider;
